import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Global internal/client "view mode" lens for the Ads Tools pages.
 *
 * This is the single source of truth for whether an operator is looking at the
 * ads tooling as themselves (internal) or previewing what the client sees
 * (client). It lives above any individual tab so the lens applies across every
 * Ads Tools page, mirroring the persist pattern used by `useTenantStore`.
 */
export type AdsViewMode = 'internal' | 'client';

interface AdsViewStore {
  viewMode: AdsViewMode;
  setViewMode: (mode: AdsViewMode) => void;
  toggleViewMode: () => void;
  /**
   * Ephemeral operator "preview as tier" override, applied ONLY while in
   * client view. `null` = show the client's real tier. Never writes the DB;
   * it only projects the KPI dashboard's gating for a what-you-see preview.
   */
  previewTier: number | null;
  setPreviewTier: (tier: number | null) => void;
}

export const useAdsViewStore = create<AdsViewStore>()(
  persist(
    (set) => ({
      viewMode: 'internal',

      setViewMode: (mode) => set({ viewMode: mode }),

      toggleViewMode: () =>
        set((state) => ({
          viewMode: state.viewMode === 'internal' ? 'client' : 'internal',
        })),

      previewTier: null,

      setPreviewTier: (tier) => set({ previewTier: tier }),
    }),
    {
      name: 'ads-view-store',
    },
  ),
);
